import { useCallback, useRef } from "react";

import { useAppState } from "@/components/AppStateProvider";
import Box from "@/components/Box";
import Divider from "@/components/Divider";
import DownloadRelatedTestProductFiles from "@/components/DownloadRelatedTestProductFiles";
import RichText from "@/components/RichText";
import AddOnDetail from "@/components/organisations/shared/ui/AddOnDetail";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { COMING_SOON, OUT_OF_STOCK, TEST_PRODUCT_LISTING_CMP_REPORTS } from "@/core/constants";
import { theme } from "@/core/theme";
import { TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION } from "@/graphql/accounts";
import { useShopPermissions } from "@/hooks/useShopPermissions";
import useUserProfile from "@/hooks/useUserProfile";
import { PanelBoxV2 } from "@/tpo/Boxes";
import { GilroyFontCurrency } from "@/tpo/Currency";
import Group from "@/tpo/Group";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import MainDetails from "@/tpo/partnerDashboard/practitioner/MainDetails";
import TestProductOptions from "@/tpo/partnerDashboard/practitioner/TestProductOptions";
import BiomarkersAndSymptomsList from "@/tpo/shop/BiomarkersAndSymptomsList";
import Carousel from "@/tpo/shop/Carousel";
import RelatedPdfs from "@/tpo/shop/RelatedFiles";
import RelatedFileClasses from "@/tpo/shop/RelatedFiles.module.css";
import TestProductInformationPanels from "@/tpo/shop/TestProductInformationPanels";
import { DISPLAY_CONTEXT_ALWAYS_VISIBLE, DISPLAY_CONTEXT_B2B_ONLY } from "@/tpo/shop/constants";
import getCarouselImages from "@/tpo/shop/testProductDetail/utils/getCarouselImages";
import { LinkButton } from "@/v2/Buttons";
import { useMutation } from "@apollo/client";

import { CMP_PERMISSION_PURCHASE_PRODUCTS } from "../../constants";
import Addons from "./Addons";
import ReportAddons from "./ReportAddons";

function ButtonDisabledExplanation({ hasClinics, optionId }) {
  const { selectedTestProductId } = useTestProductModalContext();
  const { basket, hasBasketGotProduct } = useOrganisationBasketContext();

  if (hasClinics && basket?.purchaseAsStock) {
    return (
      <Box
        color="error"
        fontFamily="gilroyMedium"
        lineHeight="150%"
        data-testid="optionUnavailable"
        fontSize={[14, 14, 16]}
      >
        Clinic options not available for stock purchases
      </Box>
    );
  }

  if (
    !basket?.purchaseAsStock &&
    !basket?.multiplePatientsEnabled &&
    hasBasketGotProduct(selectedTestProductId) &&
    !hasBasketGotProduct(selectedTestProductId, optionId)
  ) {
    return (
      <Box
        color="error"
        fontFamily="gilroyMedium"
        lineHeight="150%"
        data-testid="optionUnavailable"
        fontSize={[14, 14, 16]}
      >
        You can only have 1 option per order
      </Box>
    );
  }

  return null;
}

export default function TestProductDetail() {
  const { testProduct, selectTestProductId, selectTestProductOptionId } =
    useTestProductModalContext();
  const isStudentAllowed = !!testProduct?.isStudentAllowed;
  const studentRestrictionMessage = "Not available";

  const { organisation } = useOrganisationContext();

  const {
    basket,
    addProductToBasket,
    addOnToProduct,
    removeAddOnFromProduct,
    hasBasketGotProduct,
    hasProductGotAddon,
    setProductQtyInOrganisationBasket
  } = useOrganisationBasketContext();

  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();
  const { canPlaceOrders } = useShopPermissions();
  const permissions = organisation?.permissions || [];

  const carouselImages = getCarouselImages(testProduct);

  const handleAddToBasket = (product, option) => {
    if (!product?.isStudentAllowed) {
      return;
    }
    addProductToBasket(product.id, option?.id);
    if (basket?.multiplePatientsEnabled) {
      setBasketOpen(true);
      selectTestProductId(null);
      selectTestProductOptionId(null);
    }
  };

  const practitionerSupportRequired = !permissions.includes(CMP_PERMISSION_PURCHASE_PRODUCTS);

  const options = testProduct.options.length ? (
    <TestProductOptions>
      {testProduct.options.map((option, index) => {
        const hasClinics = !!option.practitionerClinicsInfo?.clinics?.length;
        return (
          <TestProductOptions.Option
            key={option.id}
            status={testProduct.status}
            id={option.id}
            compositeId={option.compositeId}
            name={option.testProductOptionFamily.name}
            description={option.testProductOptionFamily.description}
            price={
              hasClinics ? (
                <Stack gap={0}>
                  <GilroyFontCurrency
                    symbol={basket?.currencySymbol}
                    prefix="FROM"
                    value={option.practitionerClinicsInfo.fromPrice}
                    stacked
                    prefixFontSize={14}
                  />
                  <Box fontFamily="gilroyMedium" fontSize={14}>
                    Includes clinic fee
                  </Box>
                </Stack>
              ) : option.tradeCurrentPrice != null &&
                Number(option.tradeCurrentPrice) !== 0 ? (
                option.tradeCurrentPrice
              ) : null
            }
            previousPrice={option.exvatFullPrice}
            practitionerSupportRequired={false}
            lastOption={index === testProduct.options.length - 1}
            hasClinics={hasClinics}
            disabled={!isStudentAllowed || (hasClinics && basket?.purchaseAsStock) || !canPlaceOrders}
            disabledMessage={!isStudentAllowed ? studentRestrictionMessage : undefined}
            onAddToBasket={() => {
              handleAddToBasket(testProduct, option);
            }}
            setQty={qty => {
              setProductQtyInOrganisationBasket({
                productId: testProduct.id,
                quantity: qty,
                productOptionId: option.id
              });
            }}
          >
            <ButtonDisabledExplanation hasClinics={hasClinics} optionId={option.id} />
          </TestProductOptions.Option>
        );
      })}
    </TestProductOptions>
  ) : null;

  const informationPanels = testProduct.content.informationPanels.filter(
    panel =>
      panel.displayContext === DISPLAY_CONTEXT_ALWAYS_VISIBLE ||
      panel.displayContext === DISPLAY_CONTEXT_B2B_ONLY
  );

  const onAddToBasket = () => {
    handleAddToBasket(testProduct);
  };

  const { patientProfile } = useUserProfile();

  const setQty = useCallback(
    qty => {
      setProductQtyInOrganisationBasket({
        productId: testProduct.id,
        quantity: qty
      });
    },
    [setProductQtyInOrganisationBasket, testProduct.id]
  );

  const addons = testProduct.addons;
  const onAddingOn = ({ addon }) => {
    addOnToProduct(testProduct.id, addon.id);
  };
  const onRemovingAddon = ({ addon }) => {
    removeAddOnFromProduct(testProduct.id, addon.id);
  };

  const sampleReports = [...testProduct.sampleLabReports, ...testProduct.sampleGeneratedReports];

  const [toggleFavouriteMutation] = useMutation(TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION);

  const toggleFavourite = useCallback(
    testProductId => {
      toggleFavouriteMutation({
        variables: {
          testProductId
        }
      });
    },
    [toggleFavouriteMutation]
  );

  const hasReportAddons = addons?.some(
    addon => addon.listingVisibility === TEST_PRODUCT_LISTING_CMP_REPORTS
  );

  const showMainTradePrice =
    testProduct.tradeCurrentPrice != null &&
    Number(testProduct.tradeCurrentPrice) !== 0;

  const mainDetailsRef = useRef(null);
  const biomarkersAndSymptomsListRef = useRef(null);
  const informationPanelsRef = useRef(null);
  const downloadFilesRef = useRef(null);

  return (
    <>
      <Box display="none" data-component-name="TruncatedProductDetail" />
      <Group
        height={[43.2, 43.2, 131.2]}
        mt={[-43.2, -43.2, -131.2]}
        p={40}
        pr={[80, 80, 88]}
        gap={20}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
      >
        <LinkButton
          active
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => mainDetailsRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Overview
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() =>
            biomarkersAndSymptomsListRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        >
          Biomarkers
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => informationPanelsRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Test Details
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => downloadFilesRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Downloads
        </LinkButton>
      </Group>
      <PanelBoxV2
        ref={mainDetailsRef}
        maxWidth={768}
        outer={{
          bg: "white",
          pt: carouselImages.length ? 0 : theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          px: [20, 20, 40]
        }}
        stacked
        gap={[20, 20, 40]}
      >
        {!!carouselImages.length && (
          <Box bg="haze" p={20} mb={20}>
            <Carousel images={carouselImages} bg="haze" />
          </Box>
        )}
        <Box data-component-name="TestProductDetail:MainDetails">
          <MainDetails
            status={testProduct.status}
            isGenesTest={testProduct.isGenesTest}
            practitionerSupportRequired={practitionerSupportRequired}
            onTogglingFavourite={e => {
              e.stopPropagation();
              toggleFavourite(testProduct.id);
            }}
            favourite={patientProfile?.testProductFavouriteIds?.includes(testProduct.id)}
            name={testProduct.name}
            description={testProduct.content.description}
            categories={testProduct.content.categories.map(cat => cat.name)}
            sampleTypes={testProduct.sampleTypes}
            numOfBiomarkersTested={testProduct.content.numOfBiomarkersTested}
            processingTime={testProduct.content.processingTime}
            options={options}
            priceElement={
              <Stack>
                {showMainTradePrice ? (
                  <Group gap={10}>
                    {testProduct.tradeFullPrice !== undefined &&
                      testProduct.tradeFullPrice > testProduct.tradeCurrentPrice && (
                        <GilroyFontCurrency
                          symbol={basket?.currencySymbol}
                          value={testProduct.tradeFullPrice}
                          color="dark"
                          strikethrough
                          data-testid="truncatedProductDetail:tradeFullPrice"
                        />
                      )}
                    <GilroyFontCurrency
                      symbol={basket?.currencySymbol}
                      value={testProduct.tradeCurrentPrice}
                      data-testid="truncatedProductDetail:tradeCurrentPrice"
                      color={
                        testProduct.tradeFullPrice !== undefined &&
                        testProduct.tradeFullPrice > testProduct.tradeCurrentPrice
                          ? "red"
                          : "dark"
                      }
                    />
                  </Group>
                ) : null}
                {showMainTradePrice ? (
                  <GilroyFontCurrency
                    symbol={basket?.currencySymbol}
                    prefix="RRP"
                    color="midGrey"
                    fontSize={18}
                    decimalFontSize={18}
                    value={testProduct.exvatFullPrice}
                    data-testid="truncatedProductDetail:retailFullPrice"
                  />
                ) : null}
              </Stack>
            }
            productOptionCardStackBg="white"
            testType={testProduct.content.testType}
            onAddToBasket={onAddToBasket}
            setQty={setQty}
            centrifuge={testProduct.content.centrifuge}
            addToBasketDisabled={!isStudentAllowed}
            addToBasketDisabledMessage={!isStudentAllowed ? studentRestrictionMessage : undefined}
          />
        </Box>
      </PanelBoxV2>
      {!!addons?.length &&
        (hasReportAddons ? (
          <ReportAddons
            description={testProduct.content.reportAddonsDescription}
            addons={addons}
            onRemovingAddon={onRemovingAddon}
            onAddingOn={onAddingOn}
          />
        ) : (
          <Addons addons={addons} onRemovingAddon={onRemovingAddon} onAddingOn={onAddingOn} />
        ))}
      {!!(
        testProduct.content.relatedSymptoms?.length || testProduct.content.biomarkersTested?.length
      ) && (
        <PanelBoxV2
          ref={biomarkersAndSymptomsListRef}
          maxWidth={1020}
          outer={{
            bg: "partners",
            pt: theme.spacing.section.pt,
            pb: theme.spacing.section.pb,
            px: [20, 20, 40],
            "data-component-name": "TestProductDetail:BiomarkersAndSymptomsList"
          }}
        >
          <BiomarkersAndSymptomsList
            relatedSymptoms={testProduct.content.relatedSymptoms}
            biomarkersTested={testProduct.content.biomarkersTested}
          />
        </PanelBoxV2>
      )}
      <PanelBoxV2
        ref={informationPanelsRef}
        maxWidth={768}
        outer={{
          bg: "haze",
          pt: theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          px: [20, 20, 40],
          "data-component-name": "TestProductDetail:InformationPanels"
        }}
        stacked
        gap={40}
      >
        <TestProductInformationPanels panels={informationPanels} />
      </PanelBoxV2>
      {!!(
        sampleReports?.length ||
        testProduct.instructions?.length ||
        testProduct.supportingDocuments?.length
      ) && (
        <PanelBoxV2
          ref={downloadFilesRef}
          maxWidth={768}
          outer={{
            bg: "white",
            pt: theme.spacing.section.pt,
            pb: theme.spacing.section.pb,
            px: [20, 20, 40],
            className: RelatedFileClasses.container,
            "data-component-name": "TestProductDetail:DownloadFiles"
          }}
        >
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
            Download files
          </Box>
          {!!sampleReports?.length && (
            <RelatedPdfs sectionName="Sample Reports" pdfs={sampleReports} />
          )}
          {!!testProduct.instructions?.length && (
            <RelatedPdfs sectionName="Instructions" pdfs={testProduct.instructions} />
          )}
          {!!testProduct.supportingDocuments?.length && (
            <RelatedPdfs
              sectionName="Supporting Documents"
              pdfs={testProduct.supportingDocuments}
            />
          )}
        </PanelBoxV2>
      )}
    </>
  );
}
